import React, { useCallback, useEffect, useMemo, useState } from 'react';

import type { HotelWeightArgs } from '@this/domain/hotel_weight';
import HotelWeight from '@this/domain/hotel_weight';
import type { HotelBaseScoreArgs } from '@this/domain/hotel_base_score';
import HotelBaseScore from '@this/domain/hotel_base_score';
import { Loading } from '@this/shared/ui/feedbacks/loading/loading';
import { useAITTheme } from '@this/shared/ui/theme';
import type { ExclusionAirlineCodeResponse } from '@this/src/domain/exclusion_airline_codes';
import ExclusionAirlineCode from '@this/src/domain/exclusion_airline_codes';
import { Fetcher, HTTPError } from '@this/src/util';

import Notification from '../../../notification';
import type { InternationalAirline } from './types';
import ExclusionAirlinesForm from './exclusion_airlines_form';
import HotelBanListForm from './hotel_ban_list_form';
import HotelWeightsForm from './hotel_weights_form';
import HotelBaseScoresForm from './hotel_base_scores_form';
import { getStyles } from './repository_display_adjustment.styles';

interface InternationalAirlinesResponse {
  airlines: InternationalAirline[];
}

interface Response {
  hotel_ban_list: string;
}

interface HotelRestrictionResponse {
  hotel_restriction_list: string;
}

interface ExclusionAirlineResponse {
  airline_codes: ExclusionAirlineCodeResponse[];
}

interface HotelWeightResponse {
  hotel_weights: HotelWeightArgs[];
}

interface HotelBaseScoreResponse {
  hotel_base_scores: HotelBaseScoreArgs[];
}

const RepositoryDisplayAdjustment: React.FC = () => {
  const { theme } = useAITTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);
  const [airlines, setAirlines] = useState<InternationalAirline[]>([]);
  const [hotelBanList, setHotelBanList] = useState('');
  const [hotelRestrictionList, setHotelRestrictionList] = useState('');
  const [exclusionAirlineCodes, setExclusionAirlineCodes] = useState<ExclusionAirlineCode[]>([]);
  const [hotelWeights, setHotelWeights] = useState<HotelWeight[]>([]);
  const [hotelBaseScores, setHotelBaseScores] = useState<HotelBaseScore[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [
        { hotel_ban_list },
        { hotel_restriction_list },
        { airline_codes },
        { airlines: airlineList },
        { hotel_weights },
        { hotel_base_scores }
      ] = await Promise.all([
        Fetcher.get<Response>('/admin/repository_display_adjustment/hotel_filters'),
        Fetcher.get<HotelRestrictionResponse>(
          '/admin/repository_display_adjustment/user_attribute_plan_restrictions'
        ),
        Fetcher.get<ExclusionAirlineResponse>('/admin/repository_display_adjustment/exclusion_airlines'),
        Fetcher.get<InternationalAirlinesResponse>('/admin/international_airlines'),
        Fetcher.get<HotelWeightResponse>('/admin/repository_display_adjustment/hotel_weights.json'),
        Fetcher.get<HotelBaseScoreResponse>('/admin/repository_display_adjustment/hotel_base_scores.json')
      ]);
      setHotelBanList(hotel_ban_list);
      setHotelRestrictionList(hotel_restriction_list);
      setExclusionAirlineCodes(airline_codes.map(c => new ExclusionAirlineCode(c)));
      setAirlines(airlineList);
      setHotelWeights(hotel_weights.map(w => new HotelWeight(w)));
      setHotelBaseScores(hotel_base_scores.map(s => new HotelBaseScore(s)));
    } catch (e) {
      Notification.error(String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const updateHotelFilter = useCallback(async (banList: string) => {
    try {
      await Fetcher.put('/admin/repository_display_adjustment/hotel_filters', {
        hotel_ban_list: banList
      });
      setHotelBanList(banList);
      Notification.success('ホテル除外ワードを更新しました');
    } catch (e) {
      const message =
        e instanceof HTTPError && e.response?.data.message
          ? e.response.data.message
          : '通信環境が不安定です。\n時間をおいてもう一度お試しください。';
      Notification.error(message);
    }
  }, []);

  const updateHotelRestriction = useCallback(async (banList: string) => {
    try {
      await Fetcher.put('/admin/repository_display_adjustment/user_attribute_plan_restrictions', {
        hotel_restriction_list: banList
      });
      setHotelRestrictionList(banList);
      Notification.success('ユーザ属性による除外ワードを更新しました');
    } catch (e) {
      const message =
        e instanceof HTTPError && e.response?.data.message
          ? e.response.data.message
          : '通信環境が不安定です。\n時間をおいてもう一度お試しください。';
      Notification.error(message);
    }
  }, []);

  const updateExlusionAirlines = useCallback(async (airlineCodes: ExclusionAirlineCode[]) => {
    const params = { airline_codes: airlineCodes.map(c => c.submitParams()) };

    try {
      const { airline_codes } = await Fetcher.put<ExclusionAirlineResponse>(
        '/admin/repository_display_adjustment/exclusion_airlines/bulk_update',
        params
      );
      setExclusionAirlineCodes(airline_codes.map(c => new ExclusionAirlineCode(c)));
      Notification.success('除外海外航空券を更新しました');
    } catch (e) {
      const message = e instanceof HTTPError && e.response?.data.message ? e.response.data.message : e.message;
      Notification.error(message);
    }
  }, []);

  return (
    <div style={styles.pageLayout}>
      <h2 className="admin__title" style={styles.pageTitle}>
        在庫表示調整
      </h2>
      <div className="admin__content-in" style={styles.contentIn}>
        {loading ? (
          <Loading />
        ) : (
          <>
            <section className="e2e-hotel-filter" style={styles.section}>
              <h3 style={styles.sectionTitle}>出張に適さないプラン除外ワード</h3>
              <p style={styles.sectionDescription}>
                文字列を1行ずつ設定してください。
                <br />
                設定された文字列が含まれるホテルやプランは表示されなくなります。
              </p>
              <HotelBanListForm banList={hotelBanList} update={updateHotelFilter} />
            </section>

            <section className="e2e-hotel-user-select-filter" style={styles.section}>
              <h3 style={styles.sectionTitle}>ユーザ属性による除外ワード</h3>
              <p style={styles.sectionDescription}>
                文字列を1行ずつ設定してください。
                <br />
                プラン選択機能をONにしている場合、設定された文字列が含まれるホテルやプランは表示され、選択できますが注意文言が出ます。
                <br />
                なお、こちらに入っているワードは最初の施設の候補には選ばれません。
                <br />
                事前申請機能を利用している場合、在庫売り切れ時こちらのワードが含まれるプランは再提案の候補に含まれません。
                <br />
                「出張に適さないプラン除外ワード」にも同じワードが含まれている場合、「ホテル除外ワード」が優先されます。
              </p>
              <HotelBanListForm banList={hotelRestrictionList} update={updateHotelRestriction} />
            </section>

            <section className="e2e-airline" style={styles.section}>
              <h3 style={styles.sectionTitle}>除外海外航空券</h3>
              <p style={styles.sectionDescription}>
                リストから対象の航空会社を選択してください。
                <br />
                設定された航空会社が検索時に表示されなくなります。
              </p>
              <ExclusionAirlinesForm
                airlines={airlines}
                exclusionAirlineCodes={exclusionAirlineCodes}
                onUpdate={updateExlusionAirlines}
              />
            </section>

            <section className="e2e-hotel-weight" style={styles.section}>
              <h3 style={styles.sectionTitle}>ホテル表示重み付け</h3>
              <p style={styles.sectionDescription}>各評価値の重みを0~100の値で指定してください。</p>
              <HotelWeightsForm weights={hotelWeights} />
            </section>

            <section className="e2e-hotel-base-score" style={{ ...styles.section, ...styles.sectionLast }}>
              <h3 style={styles.sectionTitle}>ホテル基本スコア</h3>
              <p style={styles.sectionDescription}>各プロバイダの基本スコアを0~5の値で指定してください。</p>
              <HotelBaseScoresForm scores={hotelBaseScores} />
            </section>
          </>
        )}
      </div>
    </div>
  );
};

export default RepositoryDisplayAdjustment;
